import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchJobById } from '../../api/jobs';
import { saveProductPhotos, getProductPhotos } from '../../utils/inventoryStorage';
import { compressImage, COMPRESS_PRESETS } from '../../utils/imageCompressor';
import api from '../../api/http';

// 自動保存狀態
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface PhotoPreview {
  file: File;
  preview: string;
}

interface ServiceData {
  serviceTypeCode: string;
  beforePhotos: PhotoPreview[];
  afterPhotos: PhotoPreview[];
  notes: string;
  completed: boolean;
}

interface ProductServiceProgress {
  productStatus: 'normal' | 'damaged' | 'not_found'; // 產品狀態（產品級別）
  productQuantity: number; // 產品數量（產品級別）
  services: {
    [serviceTypeCode: string]: {
      completed: boolean;
      hasPhotos: boolean;
      notes?: string;
    };
  };
  allCompleted: boolean;
  damagePhotoCount?: number;
}

const ProductServicePage: React.FC = () => {
  const navigate = useNavigate();
  const { jobId, storeId, productId } = useParams<{ jobId: string; storeId: string; productId: string }>();

  // 從 localStorage 讀取已保存的進度
  const loadProgress = (): ProductServiceProgress | null => {
    if (!jobId || !storeId || !productId) return null;
    const key = `job_${jobId}_store_${storeId}_progress`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const allProgress = JSON.parse(saved);
        return allProgress.products?.[productId] || null;
      } catch (e) {
        console.error('Failed to parse progress:', e);
      }
    }
    return null;
  };

  // 獲取 Job 詳情（包含服務類型和產品）
  const {
    data: job,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => fetchJobById(jobId as string),
    enabled: Boolean(jobId),
  });

  // 找到當前產品（包括臨時添加的產品）
  const [product, setProduct] = useState<any>(null);
  
  useEffect(() => {
    if (!job || !jobId || !storeId || !productId) return;
    
    // 先從 job_products 中查找
    const jobProduct = job.job_products?.find(
      jp => jp.product_id === Number(productId) && jp.store_id === Number(storeId)
    );
    
    if (jobProduct) {
      setProduct(jobProduct.product);
    } else {
      // 如果找不到，檢查是否為臨時添加的產品
      const key = `job_${jobId}_store_${storeId}_progress`;
      const saved = localStorage.getItem(key);
      if (saved) {
        try {
          const allProgress = JSON.parse(saved);
          const additionalProduct = allProgress.additionalProducts?.find((p: any) => p.id === Number(productId));
          if (additionalProduct) {
            setProduct(additionalProduct);
          }
        } catch (e) {
          console.error('Failed to parse progress:', e);
        }
      }
    }
  }, [job, jobId, storeId, productId]);

  // 初始化服務數據狀態
  const [serviceDataMap, setServiceDataMap] = useState<{ [key: string]: ServiceData }>({});
  
  // 產品狀態（產品級別）
  const [productStatus, setProductStatus] = useState<'normal' | 'damaged' | 'not_found'>('normal');
  
  // 產品數量（產品級別）
  const [productQuantity, setProductQuantity] = useState<number>(1);
  
  // 損壞照片（產品級別，獨立於服務）
  const [damagePhotos, setDamagePhotos] = useState<PhotoPreview[]>([]);
  const damagePhotoInputRef = useRef<HTMLInputElement>(null);
  
  // 自動保存狀態
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 當 job 和 product 載入後，初始化服務數據
  useEffect(() => {
    if (!job || !product) return;
    
    const savedProgress = loadProgress();
    const initialServiceData: { [key: string]: ServiceData } = {};
    
    // 載入產品狀態和數量
    setProductStatus(savedProgress?.productStatus ?? 'normal');
    setProductQuantity(savedProgress?.productQuantity ?? 1);
    
    // 載入損壞照片
    if (jobId && storeId && productId) {
      const savedDamagePhotos = getProductPhotos(Number(jobId), Number(storeId), Number(productId), 'damage', undefined);
      if (savedDamagePhotos.length > 0) {
        setDamagePhotos(savedDamagePhotos.map(file => ({
          file,
          preview: URL.createObjectURL(file),
        })));
      }
    }
    
    // 為每個服務類型初始化數據
    job.service_items?.forEach((serviceItem: any) => {
      const serviceCode = serviceItem.service_type;
      const savedService = savedProgress?.services?.[serviceCode];
      
      initialServiceData[serviceCode] = {
        serviceTypeCode: serviceCode,
        beforePhotos: [],
        afterPhotos: [],
        notes: savedService?.notes ?? '',
        completed: savedService?.completed ?? false,
      };
    });
    
    setServiceDataMap(initialServiceData);
    
    // 載入已保存的照片
    if (jobId && storeId && productId) {
      job.service_items?.forEach((serviceItem: any) => {
        const serviceCode = serviceItem.service_type;
        const beforePhotos = getProductPhotos(Number(jobId), Number(storeId), Number(productId), serviceCode, 'before');
        const afterPhotos = getProductPhotos(Number(jobId), Number(storeId), Number(productId), serviceCode, 'after');
        
        if (beforePhotos.length > 0 || afterPhotos.length > 0) {
          setServiceDataMap(prev => ({
            ...prev,
            [serviceCode]: {
              ...prev[serviceCode],
              beforePhotos: beforePhotos.map(file => ({
                file,
                preview: URL.createObjectURL(file),
              })),
              afterPhotos: afterPhotos.map(file => ({
                file,
                preview: URL.createObjectURL(file),
              })),
            },
          }));
        }
      });
    }
  }, [job, product, jobId, storeId, productId]);

  // 自動保存到服務器
  const autoSaveToServer = useCallback(async () => {
    if (!jobId || !storeId || !productId) return;
    
    setSaveStatus('saving');
    
    try {
      // 構建服務數據
      const servicesData: { [key: string]: any } = {};
      Object.entries(serviceDataMap).forEach(([serviceCode, serviceData]) => {
        servicesData[serviceCode] = {
          completed: serviceData.completed,
          hasPhotos: serviceData.beforePhotos.length > 0 && serviceData.afterPhotos.length > 0,
          notes: serviceData.notes,
        };
      });

      // 檢查是否所有服務都完成
      const allCompleted = Object.values(servicesData).every((s: any) => s.hasPhotos);

      await api.post('/service-drafts/save', {
        job_id: Number(jobId),
        store_id: Number(storeId),
        product_id: Number(productId),
        product_status: productStatus,
        product_quantity: productQuantity,
        services_data: servicesData,
        all_completed: allCompleted,
      });

      setSaveStatus('saved');
      setLastSaveTime(new Date());
      
      // 3秒後重置狀態
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('自動保存失敗:', error);
      setSaveStatus('error');
      // 5秒後重置狀態
      setTimeout(() => setSaveStatus('idle'), 5000);
    }
  }, [jobId, storeId, productId, serviceDataMap, productStatus, productQuantity]);

  // 觸發自動保存（帶防抖）
  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveToServer();
    }, 2000); // 2秒後自動保存
  }, [autoSaveToServer]);

  // 上傳照片到服務器
  const uploadPhotoToServer = async (file: File, photoKey: string): Promise<string | null> => {
    if (!jobId || !storeId || !productId) return null;

    try {
      const formData = new FormData();
      formData.append('job_id', jobId);
      formData.append('store_id', storeId);
      formData.append('product_id', productId);
      formData.append('photo_key', photoKey);
      formData.append('photo', file);

      const response = await api.post('/service-drafts/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return response.data.url;
    } catch (error) {
      console.error('照片上傳失敗:', error);
      return null;
    }
  };

  // 從服務器加載數據（草稿和已提交的記錄合併）
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [isSubmittedRecord, setIsSubmittedRecord] = useState(false);
  
  useEffect(() => {
    const loadDataFromServer = async () => {
      if (!jobId || !storeId || !productId || !job || Object.keys(serviceDataMap).length === 0) return;
      if (draftLoaded) return; // 防止重複加載

      try {
        // 用於收集所有照片
        let allPhotosUrls: { [key: string]: string[] } = {};
        let loadedProductStatus: 'normal' | 'damaged' | 'not_found' = 'normal';
        let loadedProductQuantity = 1;
        let loadedServicesData: { [key: string]: any } = {};

        // 1. 先檢查是否有已提交的記錄
        const submittedResponse = await api.get('/service-records/product', {
          params: { job_id: jobId, store_id: storeId, product_id: productId },
        });

        if (submittedResponse.data.submitted) {
          const { product_status, product_quantity, services_data, photos_urls } = submittedResponse.data;
          
          loadedProductStatus = (product_status as 'normal' | 'damaged' | 'not_found') || 'normal';
          loadedProductQuantity = product_quantity || 1;
          loadedServicesData = services_data || {};
          setIsSubmittedRecord(true);
          
          // 收集已提交的照片
          if (photos_urls) {
            Object.entries(photos_urls).forEach(([key, urls]: [string, any]) => {
              allPhotosUrls[key] = [...(urls as string[])];
            });
          }
        }

        // 2. 也檢查草稿中是否有新照片（無論是否已提交）
        const draftResponse = await api.get('/service-drafts/get', {
          params: { job_id: jobId, store_id: storeId, product_id: productId },
        });

        const { draft, photos_urls: draftPhotosUrls } = draftResponse.data;
        
        if (draft) {
          // 如果沒有已提交記錄，使用草稿的狀態
          if (!submittedResponse.data.submitted) {
            loadedProductStatus = (draft.product_status as 'normal' | 'damaged' | 'not_found') || 'normal';
            loadedProductQuantity = draft.product_quantity || 1;
            loadedServicesData = draft.services_data || {};
          }
          
          // 合併草稿中的照片（追加到已提交的照片後面）
          if (draftPhotosUrls) {
            Object.entries(draftPhotosUrls).forEach(([key, urls]: [string, any]) => {
              if (!allPhotosUrls[key]) {
                allPhotosUrls[key] = [];
              }
              // 過濾掉重複的 URL
              (urls as string[]).forEach((url: string) => {
                if (!allPhotosUrls[key].includes(url)) {
                  allPhotosUrls[key].push(url);
                }
              });
            });
          }
        }

        // 3. 應用載入的數據
        setProductStatus(loadedProductStatus);
        setProductQuantity(loadedProductQuantity);

        setServiceDataMap(prev => {
          const updated = { ...prev };
          
          // 應用服務數據
          if (loadedServicesData) {
            Object.entries(loadedServicesData).forEach(([serviceCode, data]: [string, any]) => {
              if (updated[serviceCode]) {
                updated[serviceCode] = {
                  ...updated[serviceCode],
                  notes: data.notes || '',
                  completed: data.completed || false,
                };
              }
            });
          }
          
          // 應用所有照片
          Object.entries(allPhotosUrls).forEach(([key, urls]: [string, any]) => {
            if (key === 'damage') return;
            const parts = key.split('_');
            const photoType = parts.pop();
            const serviceCode = parts.join('_');
            
            if (serviceCode && photoType && updated[serviceCode]) {
              updated[serviceCode] = {
                ...updated[serviceCode],
                [photoType === 'before' ? 'beforePhotos' : 'afterPhotos']: (urls as string[]).map((url: string) => ({
                  file: new File([], 'server-photo'),
                  preview: url,
                })),
              };
            }
          });
          
          return updated;
        });
        
        // 應用損壞照片
        if (allPhotosUrls['damage'] && allPhotosUrls['damage'].length > 0) {
          setDamagePhotos(allPhotosUrls['damage'].map((url: string) => ({
            file: new File([], 'server-photo'),
            preview: url,
          })));
        }
        
        setDraftLoaded(true);
      } catch (error) {
        console.error('加載數據失敗:', error);
      }
    };

    loadDataFromServer();
  }, [jobId, storeId, productId, job, serviceDataMap, draftLoaded]);

  // 監聽數據變化，觸發自動保存
  useEffect(() => {
    if (product && Object.keys(serviceDataMap).length > 0) {
      triggerAutoSave();
    }
  }, [productStatus, productQuantity, triggerAutoSave]);

  // 清理定時器
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // 處理照片選擇（帶壓縮和自動上傳）
  const handlePhotoSelect = async (serviceCode: string, photoType: 'before' | 'after', e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setSaveStatus('saving');

    // 壓縮並處理每個文件
    for (const file of Array.from(files)) {
      try {
        // 壓縮圖片（使用標準質量）
        const compressedFile = await compressImage(file, COMPRESS_PRESETS.STANDARD);
        
        // 上傳到服務器
        const photoKey = `${serviceCode}_${photoType}`;
        const serverUrl = await uploadPhotoToServer(compressedFile, photoKey);
        
        // 讀取壓縮後的圖片以生成預覽
        const reader = new FileReader();
        reader.onloadend = () => {
          const newPhoto: PhotoPreview = {
            file: compressedFile,
            preview: serverUrl || reader.result as string,
          };
          
          setServiceDataMap(prev => ({
            ...prev,
            [serviceCode]: {
              ...prev[serviceCode],
              [photoType === 'before' ? 'beforePhotos' : 'afterPhotos']: [
                ...(photoType === 'before' ? prev[serviceCode].beforePhotos : prev[serviceCode].afterPhotos),
                newPhoto,
              ],
            },
          }));
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('圖片處理失敗:', error);
        alert(`圖片 ${file.name} 處理失敗，請重試`);
      }
    }

    setSaveStatus('saved');
    setLastSaveTime(new Date());
    setTimeout(() => setSaveStatus('idle'), 3000);

    if (e.target) {
      e.target.value = '';
    }
  };

  // 移除照片
  const removePhoto = (serviceCode: string, photoType: 'before' | 'after', index: number) => {
    setServiceDataMap(prev => ({
      ...prev,
      [serviceCode]: {
        ...prev[serviceCode],
        [photoType === 'before' ? 'beforePhotos' : 'afterPhotos']: 
          (photoType === 'before' ? prev[serviceCode].beforePhotos : prev[serviceCode].afterPhotos)
            .filter((_, i) => i !== index),
      },
    }));
  };

  // 保存服務數據
  const handleSave = async () => {
    if (!jobId || !storeId || !productId || !job) {
      alert('缺少必要信息');
      return;
    }

    // 如果產品不存在，自動完成所有服務
    if (productStatus === 'not_found') {
      const progressData: ProductServiceProgress = {
        productStatus: 'not_found',
        productQuantity: 0, // 產品不存在，數量為 0
        services: {},
        allCompleted: true,
      };

      const servicesData: { [key: string]: any } = {};
      Object.keys(serviceDataMap).forEach((serviceCode) => {
        progressData.services[serviceCode] = {
          completed: true,
          hasPhotos: false,
          notes: '產品不存在',
        };
        servicesData[serviceCode] = {
          notes: '產品不存在',
          completed: true,
          hasPhotos: false,
        };
      });

      // 先保存到服務器
      try {
        await api.post('/service-drafts/save', {
          job_id: Number(jobId),
          store_id: Number(storeId),
          product_id: Number(productId),
          product_status: 'not_found',
          product_quantity: 0,
          services_data: servicesData,
          all_completed: true,
        });
      } catch (error) {
        console.error('保存到服務器失敗:', error);
      }

      // 保存進度到 localStorage
      const key = `job_${jobId}_store_${storeId}_progress`;
      const saved = localStorage.getItem(key);
      const allProgress = saved ? JSON.parse(saved) : { products: {} };
      allProgress.products = allProgress.products || {};
      allProgress.products[productId] = progressData;
      localStorage.setItem(key, JSON.stringify(allProgress));

      // 返回店鋪服務頁
      navigate(`/jobs/${jobId}/store/${storeId}/products`);
      return;
    }

    // 正常流程：檢查是否所有服務都已完成
    let allCompleted = true;
    
    const progressData: ProductServiceProgress = {
      productStatus,
      productQuantity, // 保存產品級別的數量
      services: {},
      allCompleted: true,
    };

    Object.entries(serviceDataMap).forEach(([serviceCode, serviceData]) => {
      // 檢查服務是否完成（必須同時有前照和後照）
      const hasBeforePhotos = serviceData.beforePhotos.length > 0;
      const hasAfterPhotos = serviceData.afterPhotos.length > 0;
      const completed = hasBeforePhotos && hasAfterPhotos;
      
      progressData.services[serviceCode] = {
        completed,
        hasPhotos: hasBeforePhotos && hasAfterPhotos,
        notes: serviceData.notes,
      };
      
      if (!completed) {
        allCompleted = false;
      }
    });

    progressData.allCompleted = allCompleted;

    // 檢查：如果標記為損壞但沒有照片，提示用戶
    if (productStatus === 'damaged' && damagePhotos.length === 0) {
      const confirm = window.confirm('產品已標記為損壞，但尚未上傳損壞照片。是否繼續保存？（建議上傳照片記錄損壞情況）');
      if (!confirm) {
        return;
      }
    }

    // 保存照片到內存存儲
    Object.entries(serviceDataMap).forEach(([serviceCode, serviceData]) => {
      const beforeFiles = serviceData.beforePhotos.map(p => p.file);
      const afterFiles = serviceData.afterPhotos.map(p => p.file);
      
      if (beforeFiles.length > 0) {
        saveProductPhotos(Number(jobId), Number(storeId), Number(productId), beforeFiles, serviceCode, 'before');
      }
      if (afterFiles.length > 0) {
        saveProductPhotos(Number(jobId), Number(storeId), Number(productId), afterFiles, serviceCode, 'after');
      }
    });

    // 保存損壞照片到內存存儲
    if (damagePhotos.length > 0) {
      const damageFiles = damagePhotos.map(p => p.file);
      saveProductPhotos(Number(jobId), Number(storeId), Number(productId), damageFiles, 'damage', undefined);
    }

    // 保存進度到 localStorage（包含損壞照片數量）
    const key = `job_${jobId}_store_${storeId}_progress`;
    const saved = localStorage.getItem(key);
    const allProgress = saved ? JSON.parse(saved) : { products: {} };
    
    allProgress.products = allProgress.products || {};
    allProgress.products[productId] = {
      ...progressData,
      damagePhotoCount: productStatus === 'damaged' ? damagePhotos.length : 0,
    };
    
    localStorage.setItem(key, JSON.stringify(allProgress));

    // 返回店鋪服務頁
    navigate(`/jobs/${jobId}/store/${storeId}/products`);
  };

  if (isLoading) {
    return <div className="panel">載入中...</div>;
  }

  if (isError || !job || !product) {
    return (
      <div className="panel">
        <p>無法載入產品信息</p>
        <button onClick={() => navigate(`/jobs/${jobId}/store/${storeId}/products`)}>
          返回店鋪服務頁
        </button>
      </div>
    );
  }

  return (
    <div className="panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <button
          className="link-back"
          onClick={() => navigate(`/jobs/${jobId}/store/${storeId}/products`)}
          style={{ fontSize: '1.125rem', fontWeight: '600' }}
        >
          ← 返回產品列表
        </button>
        
        {/* 自動保存狀態顯示 */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: 500,
          padding: '0.5rem 1rem',
          borderRadius: '6px',
          backgroundColor: saveStatus === 'saving' ? '#fef3c7' : 
                          saveStatus === 'saved' ? '#d1fae5' : 
                          saveStatus === 'error' ? '#fee2e2' : '#f3f4f6',
          color: saveStatus === 'saving' ? '#92400e' : 
                 saveStatus === 'saved' ? '#065f46' : 
                 saveStatus === 'error' ? '#991b1b' : '#6b7280',
        }}>
          {saveStatus === 'saving' && (
            <>
              <span style={{ animation: 'pulse 1s infinite' }}>⏳</span>
              <span>正在保存...</span>
            </>
          )}
          {saveStatus === 'saved' && (
            <>
              <span>✓</span>
              <span>已自動保存</span>
            </>
          )}
          {saveStatus === 'error' && (
            <>
              <span>⚠️</span>
              <span>保存失敗</span>
            </>
          )}
          {saveStatus === 'idle' && lastSaveTime && (
            <>
              <span>☁️</span>
              <span>已同步到雲端</span>
            </>
          )}
          {saveStatus === 'idle' && !lastSaveTime && (
            <>
              <span>📱</span>
              <span>自動保存已啟用</span>
            </>
          )}
        </div>
      </div>

      {/* 產品信息 */}
      <div
        style={{
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem',
        }}
      >
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '0.5rem' }}>
          {product.name || product.product_name}
        </h2>
        {product.model && (
          <p style={{ fontSize: '1.125rem', fontWeight: '500', color: '#6b7280', marginBottom: '0.5rem' }}>
            型號：{product.model}
          </p>
        )}
        {product.item_category && (
          <span style={{ 
            display: 'inline-block',
            padding: '0.25rem 0.75rem', 
            backgroundColor: '#e0e7ff', 
            color: '#4f46e5', 
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: 600
          }}>
            {product.item_category.name}
          </span>
        )}
      </div>

      {/* 產品狀態選擇 - 產品級別 */}
      <div style={{ 
        marginBottom: '2rem', 
        padding: '1.5rem', 
        border: '2px solid #e5e7eb', 
        borderRadius: '8px',
        backgroundColor: 'white'
      }}>
        <h3 style={{ fontSize: '1.375rem', fontWeight: '700', marginBottom: '1rem' }}>產品狀態</h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
          {/* 正常 */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', fontSize: '1.25rem', fontWeight: '600' }}>
            <input
              type="radio"
              name="productStatus"
              value="normal"
              checked={productStatus === 'normal'}
              onChange={(e) => setProductStatus(e.target.value as 'normal' | 'damaged' | 'not_found')}
              style={{ width: '24px', height: '24px', cursor: 'pointer' }}
            />
            <span>✅ 正常</span>
          </label>
          
          {/* 損壞 */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', fontSize: '1.25rem', fontWeight: '600', color: '#dc2626' }}>
            <input
              type="radio"
              name="productStatus"
              value="damaged"
              checked={productStatus === 'damaged'}
              onChange={(e) => {
                setProductStatus(e.target.value as 'normal' | 'damaged' | 'not_found');
                // 如果勾選損壞，立即彈出拍照
                setTimeout(() => {
                  damagePhotoInputRef.current?.click();
                }, 100);
              }}
              style={{ width: '24px', height: '24px', cursor: 'pointer' }}
            />
            <span>⚠️ 產品損壞</span>
          </label>
          
          {/* 不存在 */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', fontSize: '1.25rem', fontWeight: '600', color: '#9ca3af' }}>
            <input
              type="radio"
              name="productStatus"
              value="not_found"
              checked={productStatus === 'not_found'}
              onChange={async (e) => {
                const confirmed = window.confirm('確定將產品標記為不存在嗎？\n\n這將會刪除此產品之前拍攝的所有照片。');
                if (!confirmed) return;
                
                setProductStatus('not_found');
                
                // 清除本地照片狀態
                setServiceDataMap(prev => {
                  const updated: { [key: string]: ServiceData } = {};
                  Object.entries(prev).forEach(([serviceCode, data]) => {
                    updated[serviceCode] = {
                      ...data,
                      beforePhotos: [],
                      afterPhotos: [],
                    };
                  });
                  return updated;
                });
                setDamagePhotos([]);
                
                // 清除服務器上的照片（通過保存空的 photos_data）
                if (jobId && storeId && productId) {
                  try {
                    // 獲取現有草稿
                    const response = await api.get('/service-drafts/get', {
                      params: { job_id: jobId, store_id: storeId, product_id: productId },
                    });
                    
                    const draft = response.data.draft;
                    if (draft && draft.photos_data) {
                      // 刪除服務器上的每張照片
                      for (const [photoKey, photoPaths] of Object.entries(draft.photos_data) as [string, string[]][]) {
                        for (let i = photoPaths.length - 1; i >= 0; i--) {
                          try {
                            await api.delete('/service-drafts/delete-photo', {
                              data: {
                                job_id: Number(jobId),
                                store_id: Number(storeId),
                                product_id: Number(productId),
                                photo_key: photoKey,
                                photo_index: i,
                              },
                            });
                          } catch (err) {
                            console.error('刪除照片失敗:', err);
                          }
                        }
                      }
                    }
                  } catch (error) {
                    console.error('獲取草稿失敗:', error);
                  }
                }
              }}
              style={{ width: '24px', height: '24px', cursor: 'pointer' }}
            />
            <span>❌ 產品不存在</span>
          </label>
        </div>
        
        {productStatus === 'damaged' && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24', marginBottom: '1rem' }}>
              <p style={{ margin: 0, fontSize: '1.125rem', fontWeight: '700', color: '#92400e' }}>
                ⚠️ 產品已標記為損壞，請拍照記錄損壞情況
              </p>
            </div>

            {/* 損壞照片拍攝區域 */}
            <div>
              <label className="form-label" style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                損壞照片 {damagePhotos.length > 0 && `(${damagePhotos.length} 張)`}
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <button
                  type="button"
                  onClick={() => damagePhotoInputRef.current?.click()}
                  className="btn-primary"
                  style={{ fontSize: '1.125rem', fontWeight: '700', padding: '0.75rem 1.5rem' }}
                >
                  📷 拍攝損壞照片
                </button>
              </div>
              <input
                ref={damagePhotoInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={async (e) => {
                  const files = e.target.files;
                  if (!files) return;

                  // 壓縮並處理每個文件
                  for (const file of Array.from(files)) {
                    try {
                      // 壓縮圖片（使用高質量，因為損壞照片需要細節）
                      const compressedFile = await compressImage(file, COMPRESS_PRESETS.HIGH);
                      
                      // 讀取壓縮後的圖片以生成預覽
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setDamagePhotos(prev => [...prev, {
                          file: compressedFile,
                          preview: reader.result as string,
                        }]);
                      };
                      reader.readAsDataURL(compressedFile);
                    } catch (error) {
                      console.error('圖片壓縮失敗:', error);
                      alert(`圖片 ${file.name} 處理失敗，請重試`);
                    }
                  }

                  if (e.target) {
                    e.target.value = '';
                  }
                }}
                style={{ display: 'none' }}
              />

              {damagePhotos.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.75rem' }}>
                  {damagePhotos.map((photo, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={photo.preview}
                        alt={`損壞照片 ${index + 1}`}
                        style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                      />
                      <button
                        type="button"
                        onClick={() => setDamagePhotos(prev => prev.filter((_, i) => i !== index))}
                        style={{
                          position: 'absolute', top: '4px', right: '4px',
                          backgroundColor: '#ef4444', color: 'white', border: 'none',
                          borderRadius: '50%', width: '24px', height: '24px',
                          cursor: 'pointer', fontSize: '0.875rem', fontWeight: '700',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 產品數量輸入 - 產品級別 */}
      {productStatus !== 'not_found' && (
        <div style={{ 
          marginBottom: '2rem', 
          padding: '1.5rem', 
          border: '2px solid #e5e7eb', 
          borderRadius: '8px',
          backgroundColor: 'white'
        }}>
          <h3 style={{ fontSize: '1.375rem', fontWeight: '700', marginBottom: '1rem' }}>產品數量</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label className="form-label" style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: 0 }}>
              數量 *
            </label>
            <input
              type="number"
              min="1"
              required
              value={productQuantity}
              onChange={(e) => setProductQuantity(Number(e.target.value) || 1)}
              className="form-input"
              style={{ fontSize: '1.125rem', fontWeight: '500', padding: '0.75rem', width: '150px' }}
            />
          </div>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem', marginBottom: 0 }}>
            此產品的實際數量（所有服務共用此數量）
          </p>
        </div>
      )}

      {/* 產品不存在提示 */}
      {productStatus === 'not_found' && (
        <div style={{ 
          padding: '2rem', 
          backgroundColor: '#f3f4f6', 
          borderRadius: '8px', 
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#6b7280', marginBottom: '1rem' }}>
            ❌ 產品不存在，所有服務將自動標記為跳過
          </p>
          <p style={{ fontSize: '1rem', color: '#9ca3af' }}>
            點擊「保存」按鈕即可完成此產品的記錄
          </p>
        </div>
      )}

      {/* 服務類型列表 - 只在產品存在時顯示 */}
      {productStatus !== 'not_found' && (
        <>
          <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem' }}>
            需要完成的服務
          </h3>
          
          {/* ⭐ 重要提示 */}
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            border: '2px solid #fbbf24',
            marginBottom: '1.5rem'
          }}>
            <p style={{ 
              fontSize: '1.25rem', 
              fontWeight: '700', 
              color: '#92400e', 
              margin: '0 0 0.5rem 0' 
            }}>
              ⚠️ 重要提示
            </p>
            <p style={{ 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              color: '#92400e', 
              margin: 0,
              lineHeight: '1.6'
            }}>
              每個服務項目都必須拍攝「前照」和「後照」才能保存。請確保所有照片清晰可見。
            </p>
          </div>

          {job.service_items?.map((serviceItem: any) => {
            const serviceCode = serviceItem.service_type;
            const serviceData = serviceDataMap[serviceCode];
            
            if (!serviceData) return null;

            return (
              <div
                key={serviceCode}
                style={{
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  backgroundColor: 'white',
                }}
              >
                <h4 style={{ fontSize: '1.375rem', fontWeight: '700', marginBottom: '1rem', color: '#1f2937' }}>
                  {serviceCode === 'inventory' ? '盤點' : 
                   serviceCode === 'pop_replacement' ? 'POP 更換' : 
                   serviceCode === 'cleaning' ? '清潔' : serviceCode}
                </h4>

                {/* 照片上傳 - 前照 */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label" style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                前照
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.multiple = true;
                    input.onchange = (e) => handlePhotoSelect(serviceCode, 'before', e as any);
                    input.click();
                  }}
                  className="btn-secondary"
                  style={{ fontSize: '1.125rem', fontWeight: '700', padding: '0.75rem 1.5rem' }}
                >
                  選擇前照
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.capture = 'environment' as any;
                    input.onchange = (e) => handlePhotoSelect(serviceCode, 'before', e as any);
                    input.click();
                  }}
                  className="btn-secondary"
                  style={{ fontSize: '1.125rem', fontWeight: '700', padding: '0.75rem 1.5rem' }}
                >
                  拍前照
                </button>
              </div>

              {serviceData.beforePhotos.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.75rem' }}>
                  {serviceData.beforePhotos.map((photo, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={photo.preview}
                        alt={`前照 ${index + 1}`}
                        style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(serviceCode, 'before', index)}
                        style={{
                          position: 'absolute', top: '4px', right: '4px',
                          backgroundColor: '#ef4444', color: 'white', border: 'none',
                          borderRadius: '50%', width: '24px', height: '24px',
                          cursor: 'pointer', fontSize: '0.875rem', fontWeight: '700',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 照片上傳 - 後照 */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label" style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                後照
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.multiple = true;
                    input.onchange = (e) => handlePhotoSelect(serviceCode, 'after', e as any);
                    input.click();
                  }}
                  className="btn-secondary"
                  style={{ fontSize: '1.125rem', fontWeight: '700', padding: '0.75rem 1.5rem' }}
                >
                  選擇後照
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.capture = 'environment' as any;
                    input.onchange = (e) => handlePhotoSelect(serviceCode, 'after', e as any);
                    input.click();
                  }}
                  className="btn-secondary"
                  style={{ fontSize: '1.125rem', fontWeight: '700', padding: '0.75rem 1.5rem' }}
                >
                  拍後照
                </button>
              </div>

              {serviceData.afterPhotos.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.75rem' }}>
                  {serviceData.afterPhotos.map((photo, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={photo.preview}
                        alt={`後照 ${index + 1}`}
                        style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(serviceCode, 'after', index)}
                        style={{
                          position: 'absolute', top: '4px', right: '4px',
                          backgroundColor: '#ef4444', color: 'white', border: 'none',
                          borderRadius: '50%', width: '24px', height: '24px',
                          cursor: 'pointer', fontSize: '0.875rem', fontWeight: '700',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 備註 */}
            <div>
              <label className="form-label" style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                備註
              </label>
              <textarea
                value={serviceData.notes}
                onChange={(e) => {
                  setServiceDataMap(prev => ({
                    ...prev,
                    [serviceCode]: {
                      ...prev[serviceCode],
                      notes: e.target.value,
                    },
                  }));
                }}
                rows={3}
                className="form-textarea"
                placeholder="輸入備註..."
                style={{ fontSize: '1.125rem', fontWeight: '500', padding: '0.75rem' }}
              />
            </div>
              </div>
            );
          })}
        </>
      )}

      {/* 操作按鈕 */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
        <button
          type="button"
          onClick={() => navigate(`/jobs/${jobId}/store/${storeId}/products`)}
          className="btn-secondary"
          style={{ flex: 1, fontSize: '1.125rem', fontWeight: '700', padding: '0.75rem 1.5rem' }}
        >
          取消
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="btn-primary"
          style={{ flex: 1, fontSize: '1.125rem', fontWeight: '700', padding: '0.75rem 1.5rem' }}
        >
          保存並返回
        </button>
      </div>
    </div>
  );
};

export default ProductServicePage;

